import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tags } from 'lucide-react';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import type { ITag } from '../../../../models/hospital/HospitalModel';
import { hospitalService } from '../../../../services/modules/hospital/hospital.service';
import styles from '../HospitalManagement.module.css';

type SelectTagFormValues = {
  tagId: string | number;
};

const selectTagInitialValues: SelectTagFormValues = {
  tagId: '',
};

const selectTagSchema = Yup.object({
  tagId: Yup.mixed<string | number>().required('Lütfen bir etiket seçin'),
});

const SelectTagsPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore } = useStore();
  const navigate = useNavigate();
  const [loadedTags, setLoadedTags] = useState<ITag[]>([]);
  const [tags, setTags] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);

  const selectedHospitalId = hospitalStore.selectedHospitalForTags?.id;

  const loadHospitalTags = useCallback(async () => {
    if (!selectedHospitalId) {
      return;
    }

    setLoading(true);
    try {
      const response = await hospitalService.getTagsByHospitalId(selectedHospitalId);

      if (response && Array.isArray(response)) {
        setLoadedTags(response);
        const tagOptions = response.map((tag: ITag) => ({
          value: tag.id.toString(),
          label: `${tag.prefix}`,
        }));
        setTags(tagOptions);
      } else {
        setLoadedTags([]);
        setTags([]);
        toast.warning('Bu hastane için etiket bulunamadı');
      }
    } catch (error) {
      console.error('Etiketler yüklenirken hata:', error);
      toast.error('Etiketler yüklenirken bir hata oluştu');
      setLoadedTags([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [selectedHospitalId]);

  useEffect(() => {
    if (popupStore.isSelectTagsOpen && selectedHospitalId) {
      loadHospitalTags();
    }
  }, [popupStore.isSelectTagsOpen, selectedHospitalId, loadHospitalTags]);

  const handleClose = () => {
    popupStore.setIsSelectTagsOpen(false);
    hospitalStore.setSelectedHospitalForTags(null);
  };

  const handleSubmit = (values: SelectTagFormValues) => {
    if (!values.tagId) {
      toast.error('Lütfen bir etiket seçin');
      return;
    }

    if (!hospitalStore.selectedHospitalForTags) {
      toast.error('Hastane bilgisi bulunamadı');
      return;
    }

    hospitalFilesStore.setSelectedHospital(hospitalStore.selectedHospitalForTags);

    const foundTag = loadedTags.find(tag => tag.id.toString() === values.tagId.toString());
    if (foundTag) {
      hospitalFilesStore.setSelectedTag(foundTag);
    }

    navigate('/hospital-files');
    handleClose();
  };

  const tagOptions = useMemo(
    () => tags.map(tag => ({ value: tag.value, label: tag.label })),
    [tags]
  );

  return (
    <Formik
      initialValues={selectTagInitialValues}
      validationSchema={selectTagSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => (
        <Popup
          isOpen={popupStore.isSelectTagsOpen}
          onClose={handleClose}
          title="Etiket Seç"
          description="Hastane dosyalarına gitmek için etiket seçin"
          variant="info"
          icon={<Tags size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter onClose={handleClose} saveLabel="Git" submittingLabel="Yönlendiriliyor..." />
          )}
        >
          <div className={styles.selectTagsContainer}>
            <div className={styles.hospitalInfo}>
              <h4>Hastane: {hospitalStore.selectedHospitalForTags?.hospitalName}</h4>
            </div>

            <Form>
              <div className={styles.dropdownContainer}>
                <FormikDropdown
                  name="tagId"
                  options={tagOptions}
                  label="Etiket Seçin"
                  placeholder={loading ? 'Yükleniyor...' : 'Etiket seçin...'}
                  disabled={loading || tagOptions.length === 0}
                  startIcon={<Tags size={18} strokeWidth={2} />}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(SelectTagsPopup);
